<template>
  <main
    class="pt-header86 theme-transition min-h-screen bg-background text-primary"
  >
    <!-- Hero Section -->
    <section class="relative py-30 px-4 min-md:px-30 bg-background">
      <div class="max-w-5xl mx-auto text-center">
        <h1 class="text-4xl min-md:text-5xl font-bold mb-10">
          <span class="text-brand-yellow">{{ $t("common.company.name") }}</span
          >:
          <span class="text-status-info">{{
            $t("ourMission.hero.title", { company: $t("common.company.name") })
          }}</span>
        </h1>
        <p class="text-xl min-md:text-2xl text-primary max-w-170 mx-auto">
          {{ $t("ourMission.hero.subtitle") }}
        </p>
      </div>
    </section>

    <!-- Dynamic Section Component -->
    <section
      v-for="(section, index) in sections"
      :key="index"
      class="border-t-2 border-status-info py-30 px-4 min-md:px-30"
      :class="section.background"
    >
      <div class="max-w-5xl mx-auto text-center">
        <h2 class="text-3xl min-md:text-4xl font-bold mb-16 text-invertedAlt">
          {{ section.title }}
        </h2>

        <!-- Grid Layouts -->
        <div
          v-if="section.grid"
          class="grid min-md:grid-cols-3 gap-4 text-center"
        >
          <div
            v-for="(item, itemIndex) in section.items"
            :key="itemIndex"
            class="p-6 rounded-lg"
            :class="item.cardClass"
          >
            <div
              class="w-16 h-16 rounded-full mb-6 mx-auto flex items-center justify-center"
              :class="item.iconClass"
            >
              <span class="text-2xl">{{ item.icon }}</span>
            </div>
            <h3 class="font-bold text-xl text-primary mb-4">
              {{ item.title }}
            </h3>
            <p class="text-secondary">
              {{ item.description }}
            </p>
          </div>
        </div>

        <!-- Mission Statement Special Layout -->
        <div
          v-else-if="section.type === 'mission'"
          class="grid min-md:grid-cols-2 gap-8"
        >
          <div
            v-for="(item, itemIndex) in section.items"
            :key="itemIndex"
            class="bg-background text-secondary rounded-2xl p-6 min-md:p-8 shadow-lg"
          >
            <h3 class="text-xl font-bold text-primary mb-4 text-center">
              <i :class="item.iconClass" class="mr-2"></i>
              {{ item.title }}
            </h3>
            <p
              class="text-justify"
              v-for="(paragraph, pIndex) in item.description"
              :key="pIndex"
            >
              {{ paragraph }}
            </p>
          </div>
        </div>

        <!-- CTA Section -->
        <div v-else-if="section.type === 'cta'" class="max-w-170 mx-auto">
          <p class="text-lg text-primary mb-20">
            {{ section.content }}
          </p>
          <router-link
            v-if="isAuthenticated"
            to="/register-service-or-space"
            class="border border-brand-yellow bg-brand-darkblue text-white px-4 py-3 rounded-lg font-medium hover:text-brand-yellow focus-visible:text-brand-yellow transition-colors duration-200"
          >
            {{ section.authenticatedButton }}
          </router-link>
          <router-link
            v-else
            to="/sign-up"
            class="border border-brand-yellow bg-brand-darkblue text-white px-4 py-3 rounded-lg font-medium hover:text-brand-yellow focus-visible:text-brand-yellow transition-colors duration-200"
          >
            {{ section.guestButton }}
          </router-link>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),
    sections() {
      return [
        {
          title: this.$t("ourMission.sections.revolution.title"),
          background: "bg-surface",
          type: "mission",
          items: [
            {
              iconClass: "fa-solid fa-globe",
              title: this.$t("ourMission.sections.revolution.items[0].title"),
              description: [
                this.$t(
                  "ourMission.sections.revolution.items[0].description[0]",
                ),
                this.$t(
                  "ourMission.sections.revolution.items[0].description[1]",
                ),
              ],
            },
            {
              iconClass: "fa-solid fa-magnifying-glass",
              title: this.$t("ourMission.sections.revolution.items[1].title"),
              description: [
                this.$t(
                  "ourMission.sections.revolution.items[1].description[0]",
                ),
                this.$t(
                  "ourMission.sections.revolution.items[1].description[1]",
                ),
              ],
            },
          ],
        },
        {
          title: this.$t("ourMission.sections.integrity.title"),
          background: "bg-background",
          grid: true,
          items: [
            {
              icon: "🤝",
              title: this.$t("ourMission.sections.integrity.items[0].title"),
              description: this.$t(
                "ourMission.sections.integrity.items[0].description",
              ),
              cardClass: "bg-surface",
              iconClass: "bg-brand-darkblue",
            },
            {
              icon: "🔏",
              title: this.$t("ourMission.sections.integrity.items[1].title"),
              description: this.$t(
                "ourMission.sections.integrity.items[1].description",
              ),
              cardClass: "bg-surface",
              iconClass: "bg-brand-darkblue",
            },
            {
              icon: "⚡",
              title: this.$t("ourMission.sections.integrity.items[2].title"),
              description: this.$t(
                "ourMission.sections.integrity.items[2].description",
              ),
              cardClass: "bg-surface",
              iconClass: "bg-brand-darkblue",
            },
          ],
        },
        {
          title: this.$t("ourMission.sections.security.title"),
          background: "bg-surface",
          grid: true,
          items: [
            {
              icon: "🔐",
              title: this.$t("ourMission.sections.security.items[0].title"),
              description: this.$t(
                "ourMission.sections.security.items[0].description",
              ),
              cardClass: "bg-background",
              iconClass: "bg-brand-darkblue",
            },
            {
              icon: "🛡️",
              title: this.$t("ourMission.sections.security.items[1].title"),
              description: this.$t(
                "ourMission.sections.security.items[1].description",
              ),
              cardClass: "bg-background",
              iconClass: "bg-brand-darkblue",
            },
            {
              icon: "📦",
              title: this.$t("ourMission.sections.security.items[2].title"),
              description: this.$t(
                "ourMission.sections.security.items[2].description",
              ),
              cardClass: "bg-background",
              iconClass: "bg-brand-darkblue",
            },
          ],
        },
        {
          title: this.$t("ourMission.sections.advantages.title", {
            company: this.$t("common.company.name"),
          }),
          background: "bg-background",
          grid: true,
          items: [
            {
              icon: "🚛",
              title: this.$t("ourMission.sections.advantages.items[0].title"),
              description: this.$t(
                "ourMission.sections.advantages.items[0].description",
              ),
              cardClass: "bg-surface",
              iconClass: "bg-brand-darkblue",
            },
            {
              icon: "🏭",
              title: this.$t("ourMission.sections.advantages.items[1].title"),
              description: this.$t(
                "ourMission.sections.advantages.items[1].description",
              ),
              cardClass: "bg-surface",
              iconClass: "bg-brand-darkblue",
            },
            {
              icon: "🎯",
              title: this.$t("ourMission.sections.advantages.items[2].title"),
              description: this.$t(
                "ourMission.sections.advantages.items[2].description",
              ),
              cardClass: "bg-surface",
              iconClass: "bg-brand-darkblue",
            },
          ],
        },
        {
          title: this.$t("ourMission.sections.cta.title"),
          background: "bg-surface",
          type: "cta",
          content: this.$t("ourMission.sections.cta.content"),
          authenticatedButton: this.$t(
            "ourMission.sections.cta.authenticatedButton",
          ),
          guestButton: this.$t("ourMission.sections.cta.guestButton"),
        },
      ];
    },
  },
};
</script>

<style scoped></style>
